import React from 'react';
import { CheckCircle, XCircle, Download, CreditCard, FileText } from 'lucide-react';
import { formatDisplayMoney } from '../../../utils/formatMoney';

const PaymentSummary = ({
  booking,
  totalPaid,
  depositPaid,
  fullPaymentPaid,
  payments,
  onDownloadInvoice,
  onGenerateReceipt,
  isGeneratingReceipt = false,
  onViewPaymentHistory,
  isPrivateBooking = false,
  onMarkOfflinePaid,
  isMarkingOfflinePaid = false,
  remainingBalance: remainingBalanceProp,
  displayCurrency = 'USD',
  displayLocale = 'en-US',
}) => {
  const money = (n) => formatDisplayMoney(n, displayCurrency, displayLocale);
  const vendorPayout = parseFloat(booking?.vendor_payout_amount || booking?.subtotal_amount || 0);
  const serviceAmountReceived = parseFloat(totalPaid || 0);
  const remainingBalance =
    remainingBalanceProp != null
      ? parseFloat(remainingBalanceProp)
      : Math.max(0, vendorPayout - serviceAmountReceived);
  const isFullyPaid = serviceAmountReceived >= vendorPayout && vendorPayout > 0;
  const showDepositStatus = depositPaid > 0 && !isFullyPaid;

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
      <div className="mb-4">
        <h2 className="text-base font-bold text-gray-900">Payment</h2>
        <p className="text-sm text-gray-500 mt-0.5">Earnings for this booking</p>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-baseline gap-3 pb-3 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-600">Service amount</span>
          <span className="text-xl font-bold tracking-tight text-gray-900">
            {money(vendorPayout)}
          </span>
        </div>

        {(parseFloat(booking?.discount_amount) || 0) > 0 && (
          <div className="flex justify-between items-center gap-3 text-sm">
            <span className="text-gray-500">
              Discount{booking?.discount_code?.code ? ` (${booking.discount_code.code})` : ''}
            </span>
            <span className="font-medium text-emerald-600">
              −{money(booking.discount_amount)}
            </span>
          </div>
        )}

        <div className="flex justify-between items-center gap-3 text-sm">
          <span className="text-gray-500">Total received</span>
          <span className="font-semibold text-gray-900">{money(totalPaid)}</span>
        </div>

        {showDepositStatus && (
          <div className="flex justify-between items-center gap-3 text-sm">
            <span className="text-gray-500">Deposit paid</span>
            <span className="font-medium text-gray-900">{money(depositPaid)}</span>
          </div>
        )}

        {fullPaymentPaid && (
          <div className="flex justify-between items-center gap-3 text-sm">
            <span className="text-gray-500">Full payment</span>
            <span className="font-medium text-gray-900 inline-flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
              Paid
            </span>
          </div>
        )}

        <div className="flex justify-between items-center gap-3 text-sm">
          <span className="text-gray-500">Status</span>
          <span
            className={`font-semibold inline-flex items-center gap-1 ${
              isFullyPaid
                ? 'text-emerald-600'
                : serviceAmountReceived > 0
                  ? 'text-amber-600'
                  : 'text-gray-500'
            }`}
          >
            {isFullyPaid ? (
              <>
                <CheckCircle className="w-3.5 h-3.5" />
                Fully paid
              </>
            ) : serviceAmountReceived > 0 ? (
              <>
                <XCircle className="w-3.5 h-3.5" />
                Partial
              </>
            ) : (
              <>
                <XCircle className="w-3.5 h-3.5" />
                Pending
              </>
            )}
          </span>
        </div>

        {remainingBalance > 0 && (
          <div className="flex justify-between items-center gap-3 pt-3 border-t border-gray-100 text-sm">
            <span className="text-gray-500">Balance due</span>
            <span className="font-semibold text-gray-900">{money(remainingBalance)}</span>
          </div>
        )}
      </div>

      <div className="mt-5 pt-5 border-t border-gray-100 space-y-2.5">
        {isPrivateBooking && !isFullyPaid && onMarkOfflinePaid && (
          <button
            type="button"
            onClick={onMarkOfflinePaid}
            disabled={isMarkingOfflinePaid}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-4 h-4" />
            {isMarkingOfflinePaid
              ? 'Recording payment...'
              : remainingBalance > 0
                ? `Mark as paid (${money(remainingBalance)})`
                : 'Mark as paid'}
          </button>
        )}
        {isPrivateBooking && !isFullyPaid && (
          <p className="text-xs text-gray-500 text-center leading-relaxed">
            Record off-platform payment (cash, bank transfer, etc.) without Stripe.
          </p>
        )}
        {onGenerateReceipt && (
          <button
            type="button"
            onClick={onGenerateReceipt}
            disabled={isGeneratingReceipt}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText className="w-4 h-4" />
            {isGeneratingReceipt ? 'Generating…' : 'Generate receipt'}
          </button>
        )}
        <button
          type="button"
          onClick={onDownloadInvoice}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 border border-gray-300 text-gray-900 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <Download className="w-4 h-4" />
          Quick print
        </button>
        {payments.length > 0 && (
          <button
            type="button"
            onClick={onViewPaymentHistory}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 border border-gray-300 text-gray-900 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <CreditCard className="w-4 h-4" />
            Payment history
          </button>
        )}
      </div>
    </section>
  );
};

export default PaymentSummary;
